import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { galleryVideoClass, GalleryVideoContent } from '../../../services/video';

interface VideoEmbedProps {
  url: string;
  title: string;
}

const VideoEmbed: React.FC<VideoEmbedProps> = ({ url, title }) => {
  // YouTube
  const getYouTubeId = (url: string) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  // Facebook
  const getFacebookVideoId = (url: string) => {
    const regExp = /facebook.com\/[^/]+\/videos\/(\d+)/;
    const match = url.match(regExp);
    return match ? match[1] : null;
  };

  // TikTok
  const getTikTokId = (url: string) => {
    const regExp = /tiktok.com\/@[\w.-]+\/video\/(\d+)/;
    const match = url.match(regExp);
    return match ? match[1] : null;
  };

  // Google Drive
  const getGoogleDriveId = (url: string) => {
    const regExp = /drive.google.com\/file\/d\/(.*?)\/view/;
    const match = url.match(regExp);
    return match ? match[1] : null;
  };

  const renderVideoEmbed = () => {
    // YouTube
    const youtubeId = getYouTubeId(url);
    if (youtubeId) {
      return (
        <iframe
          className="absolute top-0 left-0 w-full h-full"
          src={`https://www.youtube.com/embed/${youtubeId}`}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      );
    }

    // Facebook
    const facebookId = getFacebookVideoId(url);
    if (facebookId) {
      return (
        <iframe
          className="absolute top-0 left-0 w-full h-full"
          src={`https://www.facebook.com/plugins/video.php?href=https://www.facebook.com/facebook/videos/${facebookId}/`}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      );
    }

    // TikTok
    const tiktokId = getTikTokId(url);
    if (tiktokId) {
      return (
        <iframe
          className="absolute top-0 left-0 w-full h-full"
          src={`https://www.tiktok.com/embed/${tiktokId}`}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      );
    }

    // Google Drive
    const driveId = getGoogleDriveId(url);
    if (driveId) {
      return (
        <iframe
          className="absolute top-0 left-0 w-full h-full"
          src={`https://drive.google.com/file/d/${driveId}/preview`}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      );
    }

    // Fallback for unsupported URLs
    return (
      <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center bg-gray-100">
        <div className="text-center p-4 bg-red-50 border border-red-200 rounded-lg max-w-md">
          <h3 className="text-red-800 font-semibold mb-2">Format Video Tidak Didukung</h3>
          <p className="text-red-600">
            Format video ini tidak didukung. Silakan gunakan video dari YouTube, Facebook, TikTok, atau Google Drive.
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="relative pb-[56.25%] h-0">
      {renderVideoEmbed()}
    </div>
  );
};

const NoVideoMessage = () => (
  <div className="flex flex-col items-center justify-center min-h-[300px] bg-gray-50 rounded-lg p-8">
    <div className="text-gray-400 mb-4">
      <svg 
        className="w-16 h-16 mx-auto"
        fill="none" 
        viewBox="0 0 24 24" 
        stroke="currentColor"
      >
        <path 
          strokeLinecap="round" 
          strokeLinejoin="round" 
          strokeWidth={1.5}
          d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
        />
      </svg>
    </div>
    <h3 className="text-xl font-medium text-gray-900 mb-2">No Gallery Videos</h3>
    <p className="text-gray-500 text-center">There are currently no videos in the gallery.</p>
  </div>
);

const GalleryVideo = () => {
  const [video, setVideo] = useState<GalleryVideoContent | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchVideo = async () => {
      try {
        const data = await galleryVideoClass.getAllGalleryVideos();
        setVideo(data[0] || null);
      } catch (err) {
        setError('Failed to load gallery videos');
      } finally {
        setIsLoading(false);
      }
    };

    fetchVideo();
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-500 py-8">
        {error}
      </div>
    );
  }

  if (!video) {
    return <NoVideoMessage />;
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <VideoEmbed url={video.link_video} title={`Video ${video.id}`} />
      </div>
    </div>
  );
};

export default GalleryVideo;